import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ValidationErrors, ValidatorFn, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { NewsitemService } from '../../../services/newsitem.service';
import { AuthService } from '../../../auth/auth.service';
import { NewsItem } from '../../../interfaces/newsitem';
import { NgxEditorModule, toDoc, toHTML, Toolbar } from 'ngx-editor';
import { Editor } from 'ngx-editor';
import { firstValueFrom } from 'rxjs';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats } from '@angular/material/core';
import { MatMomentDateModule, MomentDateAdapter } from '@angular/material-moment-adapter';

export const MY_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'DD/MM/YYYY'
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

@Component({
  selector: 'app-newsitem-edit',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatButtonModule,
    MatFormFieldModule,
    MatDialogModule,
    MatDatepickerModule,
    MatIconModule,
    MatRadioModule,
    MatSnackBarModule,
    NgxEditorModule,
    MatMomentDateModule
  ],
  templateUrl: './newsitem-edit.component.html',
  styleUrls: ['./newsitem-edit.component.scss'],
  providers: [
    { provide: DateAdapter, useClass: MomentDateAdapter },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS }
  ]
})
export class NewsitemEditComponent implements OnInit, OnDestroy {
  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private newsItemService: NewsitemService,
    private authService: AuthService,
    public dialogRef: MatDialogRef<NewsitemEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { item: NewsItem }
  ) {
    // Assign the incoming item to the local variable
    console.log(data.item);
    this.item = data.item;
  }

  newsItemForm!: FormGroup;
  username: string = 'Unknown';
  item!: NewsItem;

  //Rich text format editor values
  editor!: Editor;
  toolbar: Toolbar = [
    // default value
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
    ['undo', 'redo']
  ];
  ngOnInit(): void {
    this.editor = new Editor();
    this.getUserName();
    this.newsItemForm = this.fb.group(
      {
        id: [this.item.id],
        title: [this.item.title, Validators.required],
        content: [toDoc(this.item.content), Validators.required],
        expirationDate: [this.item.expirationDate],
        forInternal: [this.item.forInternal],
        forCenter: [this.item.forCenter],
        forLab: [this.item.forLab],
        userCreatedName: [this.username]
      },
      {
        validators: atLeastOneCheckboxValidator()
      }
    );
  }

  async getUserName() {
    const user = await firstValueFrom(this.authService.getUser());
    if (user) {
      this.username = user.username;
    }

    return this.username;
  }
  async editNewsItem() {
    this.newsItemForm.value.userCreatedName = await this.getUserName();
    this.newsItemForm.value.content = toHTML(this.newsItemForm.value.content);
    if (this.newsItemForm.invalid) {
      // Check if the error is due to the atLeastOne checkbox validation
      if (this.newsItemForm.errors?.['atLeastOne']) {
        this.showNotification('Please select at least one audience (Internal, Centers, or Laboratories).');
      } else {
        this.showNotification('Please fill out all required fields correctly.');
      }
      return;
    }

    let newsItemData = this.newsItemForm.value;
    this.newsItemService.updateNewsItem(newsItemData).subscribe({
      next: (response) => {
        console.log('News item  created:', response);
        this.showNotification('News item updated successfully!');
        this.dialogRef.close(response); // Close the dialog and return updated item
      },
      error: (err) => {
        this.showNotification('Failed to update news item. Please try again.');
      }
    });
  }

  showNotification(message: string, action: string = 'Close'): void {
    this.snackBar.open(message, action, {
      duration: 3000,
      verticalPosition: 'top',
      horizontalPosition: 'center'
    });
  }
  ngOnDestroy(): void {
    this.editor.destroy();
  }
}

export function atLeastOneCheckboxValidator(): ValidatorFn {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const forInternal = formGroup.get('forInternal')?.value;
    const forCenter = formGroup.get('forCenter')?.value;
    const forLab = formGroup.get('forLab')?.value;

    // If all checkboxes are false, return an error
    if (!forInternal && !forCenter && !forLab) {
      return { atLeastOne: true }; // Validation error
    }

    return null; // Validation passed
  };
}
